import { useState } from 'react'

import { api, ErrorApi, type FilaCatalogo } from '../api'
import { usdExacto } from '../formato'
import { CeldaCosto } from './CeldaCosto'
import { TipoDelItem } from './TipoDelItem'

interface Props {
  fila: FilaCatalogo
  /** Solo el admin edita; el servidor lo comprueba otra vez. */
  puedeEditar: boolean
  alCambiar: () => void
  alFallar: (mensaje: string) => void
}

/** Una fila: costo editable, IVA (en los propios) y Activar/Desactivar. No hay «Borrar». */
export function FilaItemCatalogo({ fila: f, puedeEditar, alCambiar, alFallar }: Props) {
  const [ocupado, setOcupado] = useState(false)

  const guardar = async (accion: () => Promise<unknown>) => {
    setOcupado(true)
    try {
      await accion()
      alCambiar()
    } catch (err) {
      alFallar(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
    } finally {
      setOcupado(false)
    }
  }

  const cambiarCosto = (costo: number) =>
    guardar(() => (f.id ? api.cambiarItem(f.id, { costo }) : api.ajustarProducto(f.codigo, { costo, activo: f.activo })))
  const restablecer = () => guardar(() => api.ajustarProducto(f.codigo, { costo: null, activo: f.activo }))
  const cambiarActivo = () =>
    guardar(() => (f.id ? api.cambiarItem(f.id, { activo: !f.activo }) : api.ajustarProducto(f.codigo, { costo: f.modificado && f.costo !== f.costo_original ? f.costo : null, activo: !f.activo })))
  const editable = puedeEditar && f.editable

  return (
    <tr className={f.activo ? 'sin-clic' : 'sin-clic fila-inactiva'}>
      <td>
        <span className="fuerte">{f.nombre}</span>
        <small className="suave bloque">
          {f.codigo} · {f.proveedor}
          {f.nota && ` · ${f.nota}`}
        </small>
      </td>
      <td>
        <TipoDelItem fila={f} puedeEditar={puedeEditar} alCambiar={alCambiar} alFallar={alFallar} />
      </td>
      <td>
        <span className={f.origen === 'propio' ? 'insignia insignia-cliente' : 'insignia'}>{f.origen === 'propio' ? 'Nuevo' : 'Motor'}</span>
      </td>
      <td className="num">
        {editable ? (
          <CeldaCosto valor={f.costo} modificado={f.modificado && f.costo !== f.costo_original} alCambiar={cambiarCosto} alRestablecer={restablecer} tituloRestablecer="Volver al precio original del motor" maximo={1_000_000} />
        ) : (
          usdExacto(f.costo)
        )}
        {f.modificado && f.costo_original !== null && f.costo !== f.costo_original && <small className="suave bloque">original {usdExacto(f.costo_original)}</small>}
      </td>
      <td>
        {f.origen === 'propio' && editable ? (
          <select value={f.grava_iva ? 'si' : 'no'} disabled={ocupado} aria-label="IVA" onChange={(e) => guardar(() => api.cambiarItem(f.id!, { grava_iva: e.target.value === 'si' }))}>
            <option value="si">Grava 15 %</option>
            <option value="no">Sin IVA</option>
          </select>
        ) : (
          <span className="suave">{f.grava_iva ? 'Grava 15 %' : 'Sin IVA'}</span>
        )}
      </td>
      <td>
        <span className={f.activo ? 'insignia insignia-aprobado' : 'insignia insignia-rechazado'}>{f.activo ? 'Activo' : 'Inactivo'}</span>
      </td>
      <td className="acciones">
        {puedeEditar && f.editable && (
          <button className="secundario pequeno" disabled={ocupado} onClick={cambiarActivo}>
            {f.activo ? 'Desactivar' : 'Activar'}
          </button>
        )}
      </td>
    </tr>
  )
}
